import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Box,
    Container,
    Typography,
    Paper,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Button,
    Divider,
    Grid,
    Tab,
    Tabs,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
    Chip,
    CircularProgress,
} from '@mui/material';
import {
    HelpCircle,
    ChevronDown,
    BookOpen,
    MessageCircle,
    Bug,
    FileText,
    Users,
    Calculator,
    BarChart3,
    Plus,
    Megaphone,
    X,
    Pencil,
    Trash2,
    AlertTriangle,
    Lightbulb,
    Layers,
    MapPin,
    ClipboardCheck,
    FolderOpen,
    Printer,
} from 'lucide-react';
import { useGetIdentity } from 'react-admin';
import BugReportDialog from '../bugs/BugReportDialog';
import { supabase } from '../providers/supabase/supabase';

// ── Types ──────────────────────────────────────────────────────

interface ProductUpdate {
    id: string;
    subject: string;
    description: string;
    created_at: string;
    updated_at: string;
    created_by: string | null;
}

interface KnownIssue {
    id: string;
    subject: string;
    description: string;
    created_at: string;
    updated_at: string;
    created_by: string | null;
}

type SortDirection = 'asc' | 'desc';

// ── Static data ────────────────────────────────────────────────

const howToGuides = [
    {
        icon: <Users size={20} color="#10b981" />,
        title: 'Managing Accounts & Contacts',
        description:
            'Learn how to add new accounts and contacts to help keep your customer records organized.',
    },
    {
        icon: <Calculator size={20} color="#10b981" />,
        title: 'Using the Hail Pricing Tool',
        description:
            'Compare rates across AIPs, generate professional quotes, and create PDF rate sheets for your farmers.',
    },
    {
        icon: <FileText size={20} color="#10b981" />,
        title: 'Creating & Managing Quotes',
        description:
            'Build multi-line quotes, track them through the pipeline, and generate polished PDFs to share with clients.',
    },
    {
        icon: <BarChart3 size={20} color="#10b981" />,
        title: 'Dashboard & Reports',
        description:
            'View key performance metrics, track your sales pipeline, and monitor quote activity at a glance.',
    },
];

const bestPractices = [
    {
        icon: <Layers size={20} color="#10b981" />,
        title: 'Keep Hail Products Focused',
        description:
            'Limit each quote to 3\u20134 hail products per location. Too many products crowd the PDF rate sheet and make it hard for farmers to read.',
    },
    {
        icon: <MapPin size={20} color="#10b981" />,
        title: 'Be Precise with Locations',
        description:
            'Use specific township/range entries rather than adding 10+ broad locations. Fewer, targeted locations produce cleaner quotes and faster rate comparisons.',
    },
    {
        icon: <ClipboardCheck size={20} color="#10b981" />,
        title: 'Name Quotes Descriptively',
        description:
            'Include the farmer name, crop, and year in the quote name (e.g. "Johnson \u2013 Spring Wheat 2026"). Makes it easy to find quotes later in the pipeline.',
    },
    {
        icon: <FolderOpen size={20} color="#10b981" />,
        title: 'One Quote per Crop Group',
        description:
            'Create separate quotes for unrelated crop groups instead of combining everything into one. This keeps rate sheets clean and makes it easier to revise individual quotes.',
    },
    {
        icon: <Printer size={20} color="#10b981" />,
        title: 'Preview Before You Print',
        description:
            'Always preview the PDF before sharing with a farmer. Check that tables fit on the page and that all rates loaded correctly \u2014 a polished quote builds trust.',
    },
    {
        icon: <Calculator size={20} color="#10b981" />,
        title: 'Compare AIPs Side-by-Side',
        description:
            'Select multiple carriers before fetching rates so you can show farmers their best options in one view. This speeds up the conversation and helps close deals faster.',
    },
];

const faqs = [
    {
        question: 'How do I add a new account?',
        answer: 'Navigate to the Accounts page from the sidebar, then click the "+ Add" button in the top right. Fill in the account details like name, address, and sector, then save.',
    },
    {
        question: 'How do I create a new contact?',
        answer: 'Go to the Contacts page from the sidebar and click the "+ Add" button. Enter the contact\'s name, email, phone, and link them to an existing account. You can also add a contact directly from an account\'s detail page.',
    },
    {
        question: 'Can I import contacts from a spreadsheet?',
        answer: 'CSV import is a premium feature available in CropAMS, our complete agency management system. With CropAMS, you can bulk-import contacts from spreadsheets, automatically map columns, and review everything before importing. Upgrade to CropAMS to unlock this and many more powerful features!',
    },
    {
        question: 'How do I compare rates from different carriers?',
        answer: 'Open the Hail Pricing Tool from the sidebar. Select the state, county, crop, and coverage details, then click "Get Rates." The tool pulls rates from all available AIPs so you can compare side-by-side.',
    },
    {
        question: 'How do I generate a PDF quote?',
        answer: 'From any quote, click the "Generate PDF" button. The system creates a professional rate sheet you can download or share directly with your farmer.',
    },
    {
        question: 'What do the quote pipeline stages mean?',
        answer: 'Quotes move through stages: Draft (just created), Presented (shared with the farmer), Negotiating (in discussion), Won (bound), or Lost. Drag quotes between columns on the Kanban board to update their status.',
    },
    {
        question: 'I forgot my password. How do I reset it?',
        answer: 'On the login page, click "Forgot Your Password?" and enter your email. You\'ll receive a reset link. If you don\'t see the email, check your spam folder or contact your administrator.',
    },
];

// ── Helpers ────────────────────────────────────────────────────

const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });

// ── Help & FAQ Tab ─────────────────────────────────────────────

function HelpFaqTab({
    bugDialogOpen,
    setBugDialogOpen,
}: {
    bugDialogOpen: boolean;
    setBugDialogOpen: (v: boolean) => void;
}) {
    return (
        <>
            <Paper
                elevation={0}
                sx={{
                    p: 3,
                    mb: 4,
                    borderRadius: '12px',
                    border: '1px solid #e2e8f0',
                    background:
                        'linear-gradient(135deg, #ecfdf5 0%, #f0fdf4 50%, #f8fafc 100%)',
                }}
            >
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={8}>
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 600,
                                color: '#1e293b',
                                mb: 0.5,
                            }}
                        >
                            Something not working right?
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b' }}>
                            Let us know and we'll fix it. Your report helps us
                            make the Hail Pricing Tool better for everyone.
                        </Typography>
                    </Grid>
                    <Grid
                        item
                        xs={12}
                        sm={4}
                        sx={{ textAlign: { xs: 'left', sm: 'right' } }}
                    >
                        <Button
                            variant="contained"
                            startIcon={<Bug size={18} />}
                            onClick={() => setBugDialogOpen(true)}
                            sx={{
                                bgcolor: '#ef4444',
                                '&:hover': { bgcolor: '#dc2626' },
                                borderRadius: '8px',
                                textTransform: 'none',
                                fontWeight: 600,
                                px: 3,
                            }}
                        >
                            Report a Bug
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            <Box sx={{ mb: 4 }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 2,
                    }}
                >
                    <BookOpen size={20} color="#10b981" />
                    <Typography
                        variant="h6"
                        sx={{ fontWeight: 600, color: '#1e293b' }}
                    >
                        How-To Guides
                    </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: '#64748b', mb: 2.5 }}>
                    Quick overviews of the main features. Detailed guides coming
                    soon!
                </Typography>
                <Grid container spacing={2}>
                    {howToGuides.map(guide => (
                        <Grid item xs={12} sm={6} key={guide.title}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2.5,
                                    borderRadius: '10px',
                                    border: '1px solid #e2e8f0',
                                    height: '100%',
                                    transition: 'border-color 0.2s',
                                    '&:hover': { borderColor: '#10b981' },
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: 1.5,
                                    }}
                                >
                                    <Box sx={{ mt: 0.25, flexShrink: 0 }}>
                                        {guide.icon}
                                    </Box>
                                    <Box>
                                        <Typography
                                            variant="subtitle2"
                                            sx={{
                                                fontWeight: 600,
                                                color: '#1e293b',
                                                mb: 0.5,
                                            }}
                                        >
                                            {guide.title}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{ color: '#64748b' }}
                                        >
                                            {guide.description}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            <Divider sx={{ mb: 4 }} />

            <Box sx={{ mb: 4 }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 2,
                    }}
                >
                    <Lightbulb size={20} color="#10b981" />
                    <Typography
                        variant="h6"
                        sx={{ fontWeight: 600, color: '#1e293b' }}
                    >
                        Best Practices
                    </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: '#64748b', mb: 2.5 }}>
                    Tips to get the most out of the Hail Pricing Tool and
                    produce clean, professional quotes.
                </Typography>
                <Grid container spacing={2}>
                    {bestPractices.map(tip => (
                        <Grid item xs={12} sm={6} key={tip.title}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2.5,
                                    borderRadius: '10px',
                                    border: '1px solid #e2e8f0',
                                    height: '100%',
                                    transition: 'border-color 0.2s',
                                    '&:hover': { borderColor: '#10b981' },
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: 1.5,
                                    }}
                                >
                                    <Box sx={{ mt: 0.25, flexShrink: 0 }}>
                                        {tip.icon}
                                    </Box>
                                    <Box>
                                        <Typography
                                            variant="subtitle2"
                                            sx={{
                                                fontWeight: 600,
                                                color: '#1e293b',
                                                mb: 0.5,
                                            }}
                                        >
                                            {tip.title}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{ color: '#64748b' }}
                                        >
                                            {tip.description}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            <Divider sx={{ mb: 4 }} />

            <Box sx={{ mb: 4 }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 2,
                    }}
                >
                    <MessageCircle size={20} color="#10b981" />
                    <Typography
                        variant="h6"
                        sx={{ fontWeight: 600, color: '#1e293b' }}
                    >
                        Frequently Asked Questions
                    </Typography>
                </Box>
                <Box>
                    {faqs.map((faq, index) => (
                        <Accordion
                            key={index}
                            elevation={0}
                            disableGutters
                            sx={{
                                border: '1px solid #e2e8f0',
                                borderRadius: '8px !important',
                                mb: 1.5,
                                '&:before': { display: 'none' },
                                '&.Mui-expanded': { borderColor: '#10b981' },
                            }}
                        >
                            <AccordionSummary
                                expandIcon={
                                    <ChevronDown size={18} color="#64748b" />
                                }
                                sx={{
                                    px: 2.5,
                                    '& .MuiAccordionSummary-content': {
                                        my: 1.5,
                                    },
                                }}
                            >
                                <Typography
                                    variant="body1"
                                    sx={{ fontWeight: 500, color: '#1e293b' }}
                                >
                                    {faq.question}
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ px: 2.5, pt: 0, pb: 2 }}>
                                <Typography
                                    variant="body2"
                                    sx={{ color: '#475569', lineHeight: 1.7 }}
                                >
                                    {faq.answer}
                                </Typography>
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </Box>
            </Box>

            <Paper
                elevation={0}
                sx={{
                    p: 3,
                    borderRadius: '12px',
                    border: '1px solid #e2e8f0',
                    textAlign: 'center',
                    bgcolor: '#f8fafc',
                }}
            >
                <Typography variant="body2" sx={{ color: '#64748b' }}>
                    Can't find what you're looking for? Reach out to your
                    administrator or email{' '}
                    <Box
                        component="a"
                        href="mailto:support@hailhelper.com"
                        sx={{
                            color: '#10b981',
                            fontWeight: 500,
                            textDecoration: 'none',
                            '&:hover': { textDecoration: 'underline' },
                        }}
                    >
                        support@hailhelper.com
                    </Box>
                </Typography>
            </Paper>

            <BugReportDialog
                open={bugDialogOpen}
                onClose={() => setBugDialogOpen(false)}
                screenshotBlob={null}
                formData={null}
            />
        </>
    );
}

// ── Shared admin CRUD table ────────────────────────────────────

function AdminCrudTable({
    items,
    loading,
    isAdmin,
    emptyIcon,
    emptyTitle,
    emptySubtitle,
    headerTitle,
    headerSubtitle,
    addButtonLabel,
    createDialogTitle,
    editDialogTitle,
    publishLabel,
    iconColor,
    Icon,
    onAdd,
    onEdit,
    onDelete,
    onView,
}: {
    items: { id: string; subject: string; created_at: string }[];
    loading: boolean;
    isAdmin: boolean;
    emptyIcon: React.ReactNode;
    emptyTitle: string;
    emptySubtitle: string;
    headerTitle: string;
    headerSubtitle: string;
    addButtonLabel: string;
    createDialogTitle: string;
    editDialogTitle: string;
    publishLabel: string;
    iconColor: string;
    Icon: React.ComponentType<any>;
    onAdd: () => void;
    onEdit: (item: any) => void;
    onDelete: (id: string) => void;
    onView: (item: any) => void;
}) {
    const [sortDir, setSortDir] = useState<SortDirection>('desc');
    const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

    const sortedItems = useMemo(() => {
        return [...items].sort((a, b) => {
            const diff =
                new Date(a.created_at).getTime() -
                new Date(b.created_at).getTime();
            return sortDir === 'asc' ? diff : -diff;
        });
    }, [items, sortDir]);

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 3,
                }}
            >
                <Box>
                    <Typography
                        variant="h6"
                        sx={{ fontWeight: 600, color: '#1e293b' }}
                    >
                        {headerTitle}
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{ color: '#64748b', mt: 0.5 }}
                    >
                        {headerSubtitle}
                    </Typography>
                </Box>
                {isAdmin && (
                    <Button
                        variant="contained"
                        startIcon={<Plus size={18} />}
                        onClick={onAdd}
                        sx={{
                            bgcolor: '#10b981',
                            '&:hover': { bgcolor: '#059669' },
                            borderRadius: '8px',
                            textTransform: 'none',
                            fontWeight: 600,
                            px: 3,
                        }}
                    >
                        {addButtonLabel}
                    </Button>
                )}
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress size={32} sx={{ color: '#10b981' }} />
                </Box>
            ) : items.length === 0 ? (
                <Paper
                    elevation={0}
                    sx={{
                        p: 6,
                        borderRadius: '12px',
                        border: '1px solid #e2e8f0',
                        textAlign: 'center',
                    }}
                >
                    {emptyIcon}
                    <Typography
                        variant="body1"
                        sx={{ color: '#94a3b8', fontWeight: 500 }}
                    >
                        {emptyTitle}
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{ color: '#cbd5e1', mt: 0.5 }}
                    >
                        {emptySubtitle}
                    </Typography>
                </Paper>
            ) : (
                <TableContainer
                    component={Paper}
                    elevation={0}
                    sx={{ borderRadius: '12px', border: '1px solid #e2e8f0' }}
                >
                    <Table>
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                <TableCell
                                    sx={{ fontWeight: 600, color: '#475569' }}
                                >
                                    Subject
                                </TableCell>
                                <TableCell
                                    sx={{
                                        fontWeight: 600,
                                        color: '#475569',
                                        width: 150,
                                    }}
                                    sortDirection={sortDir}
                                >
                                    <TableSortLabel
                                        active
                                        direction={sortDir}
                                        onClick={() =>
                                            setSortDir(d =>
                                                d === 'asc' ? 'desc' : 'asc'
                                            )
                                        }
                                    >
                                        Date
                                    </TableSortLabel>
                                </TableCell>
                                {isAdmin && (
                                    <TableCell
                                        sx={{
                                            fontWeight: 600,
                                            color: '#475569',
                                            width: 100,
                                        }}
                                        align="right"
                                    >
                                        Actions
                                    </TableCell>
                                )}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedItems.map(item => (
                                <TableRow
                                    key={item.id}
                                    hover
                                    sx={{
                                        cursor: 'pointer',
                                        '&:last-child td': { borderBottom: 0 },
                                    }}
                                    onClick={() => onView(item)}
                                >
                                    <TableCell>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                            }}
                                        >
                                            <Icon size={16} color={iconColor} />
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontWeight: 500,
                                                    color: '#1e293b',
                                                }}
                                            >
                                                {item.subject}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={formatDate(item.created_at)}
                                            size="small"
                                            sx={{
                                                bgcolor: '#f1f5f9',
                                                color: '#64748b',
                                                fontWeight: 500,
                                                fontSize: '0.75rem',
                                            }}
                                        />
                                    </TableCell>
                                    {isAdmin && (
                                        <TableCell align="right">
                                            <IconButton
                                                size="small"
                                                onClick={e => {
                                                    e.stopPropagation();
                                                    onEdit(item);
                                                }}
                                                sx={{ color: '#64748b' }}
                                            >
                                                <Pencil size={16} />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={e => {
                                                    e.stopPropagation();
                                                    setDeleteConfirmId(item.id);
                                                }}
                                                sx={{ color: '#ef4444' }}
                                            >
                                                <Trash2 size={16} />
                                            </IconButton>
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Dialog
                open={!!deleteConfirmId}
                onClose={() => setDeleteConfirmId(null)}
                PaperProps={{ sx: { borderRadius: '12px' } }}
            >
                <DialogTitle sx={{ fontWeight: 600 }}>
                    Delete Entry?
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary">
                        This will permanently remove this entry. This action
                        cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={() => setDeleteConfirmId(null)}
                        sx={{ textTransform: 'none', color: '#64748b' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => {
                            if (deleteConfirmId) onDelete(deleteConfirmId);
                            setDeleteConfirmId(null);
                        }}
                        sx={{
                            bgcolor: '#ef4444',
                            '&:hover': { bgcolor: '#dc2626' },
                            textTransform: 'none',
                            fontWeight: 600,
                            borderRadius: '8px',
                        }}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

// ── View / Edit dialogs ────────────────────────────────────────

function ViewDialog({
    item,
    onClose,
}: {
    item: { subject: string; description: string; created_at: string } | null;
    onClose: () => void;
}) {
    return (
        <Dialog
            open={!!item}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { borderRadius: '12px' } }}
        >
            {item && (
                <>
                    <DialogTitle
                        component="div"
                        sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            justifyContent: 'space-between',
                            pb: 1,
                        }}
                    >
                        <Box>
                            <Typography
                                variant="h6"
                                sx={{ fontWeight: 600, color: '#1e293b' }}
                            >
                                {item.subject}
                            </Typography>
                            <Typography
                                variant="caption"
                                sx={{ color: '#94a3b8' }}
                            >
                                {formatDate(item.created_at)}
                            </Typography>
                        </Box>
                        <IconButton onClick={onClose} size="small">
                            <X size={18} />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent>
                        <Typography
                            variant="body1"
                            sx={{
                                color: '#334155',
                                lineHeight: 1.8,
                                whiteSpace: 'pre-wrap',
                            }}
                        >
                            {item.description}
                        </Typography>
                    </DialogContent>
                </>
            )}
        </Dialog>
    );
}

function EditDialog({
    open,
    onClose,
    title,
    publishLabel,
    initialSubject,
    initialDescription,
    onSave,
}: {
    open: boolean;
    onClose: () => void;
    title: string;
    publishLabel: string;
    initialSubject: string;
    initialDescription: string;
    onSave: (subject: string, description: string) => Promise<void>;
}) {
    const [subject, setSubject] = useState(initialSubject);
    const [description, setDescription] = useState(initialDescription);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setSubject(initialSubject);
        setDescription(initialDescription);
    }, [initialSubject, initialDescription, open]);

    const handleSave = async () => {
        if (!subject.trim()) return;
        setSaving(true);
        await onSave(subject.trim(), description);
        setSaving(false);
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { borderRadius: '12px' } }}
        >
            <DialogTitle
                component="div"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}
            >
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {title}
                </Typography>
                <IconButton onClick={onClose} size="small">
                    <X size={18} />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <TextField
                    label="Subject"
                    value={subject}
                    onChange={e => setSubject(e.target.value)}
                    fullWidth
                    required
                    sx={{ mt: 1, mb: 2 }}
                    inputProps={{ maxLength: 200 }}
                />
                <TextField
                    label="Description"
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                    fullWidth
                    multiline
                    minRows={6}
                    maxRows={16}
                    placeholder="Describe the update, new features, improvements, or fixes..."
                />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button
                    onClick={onClose}
                    sx={{ textTransform: 'none', color: '#64748b' }}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={!subject.trim() || saving}
                    sx={{
                        bgcolor: '#10b981',
                        '&:hover': { bgcolor: '#059669' },
                        textTransform: 'none',
                        fontWeight: 600,
                        borderRadius: '8px',
                    }}
                >
                    {saving ? 'Saving...' : publishLabel}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

// ── Product Updates Tab ────────────────────────────────────────

function ProductUpdatesTab({ isAdmin }: { isAdmin: boolean }) {
    const [updates, setUpdates] = useState<ProductUpdate[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedUpdate, setSelectedUpdate] = useState<ProductUpdate | null>(
        null
    );
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editingUpdate, setEditingUpdate] = useState<ProductUpdate | null>(
        null
    );

    const fetchUpdates = useCallback(async () => {
        setLoading(true);
        const { data, error } = await supabase
            .from('product_updates')
            .select('*')
            .order('created_at', { ascending: false });
        if (!error && data) setUpdates(data);
        setLoading(false);
    }, []);

    useEffect(() => {
        fetchUpdates();
    }, [fetchUpdates]);

    const handleView = useCallback(async (update: ProductUpdate) => {
        setSelectedUpdate(update);
        // Mark as read for current user
        const {
            data: { user },
        } = await supabase.auth.getUser();
        if (user) {
            await supabase
                .from('product_update_reads')
                .upsert(
                    { user_id: user.id, update_id: update.id },
                    { onConflict: 'user_id,update_id' }
                );
        }
    }, []);

    const handleSave = async (subject: string, description: string) => {
        if (editingUpdate) {
            await supabase
                .from('product_updates')
                .update({ subject, description })
                .eq('id', editingUpdate.id);
        } else {
            const {
                data: { user },
            } = await supabase.auth.getUser();
            await supabase.from('product_updates').insert({
                subject,
                description,
                created_by: user?.id ?? null,
            });
        }
        fetchUpdates();
    };

    const handleDelete = async (id: string) => {
        await supabase.from('product_updates').delete().eq('id', id);
        fetchUpdates();
    };

    return (
        <>
            <AdminCrudTable
                items={updates}
                loading={loading}
                isAdmin={isAdmin}
                emptyIcon={
                    <Megaphone
                        size={40}
                        color="#cbd5e1"
                        style={{ marginBottom: 12 }}
                    />
                }
                emptyTitle="No product updates yet."
                emptySubtitle="Check back soon for new features and improvements."
                headerTitle="Product Updates"
                headerSubtitle="Stay up to date with new features, improvements, and fixes."
                addButtonLabel="Add Update"
                createDialogTitle="New Product Update"
                editDialogTitle="Edit Update"
                publishLabel="Publish Update"
                iconColor="#10b981"
                Icon={Megaphone}
                onAdd={() => {
                    setEditingUpdate(null);
                    setEditDialogOpen(true);
                }}
                onEdit={item => {
                    setEditingUpdate(item as ProductUpdate);
                    setEditDialogOpen(true);
                }}
                onDelete={handleDelete}
                onView={item => handleView(item as ProductUpdate)}
            />

            <ViewDialog
                item={selectedUpdate}
                onClose={() => setSelectedUpdate(null)}
            />

            <EditDialog
                open={editDialogOpen}
                onClose={() => setEditDialogOpen(false)}
                title={editingUpdate ? 'Edit Update' : 'New Product Update'}
                publishLabel={editingUpdate ? 'Save Changes' : 'Publish Update'}
                initialSubject={editingUpdate?.subject ?? ''}
                initialDescription={editingUpdate?.description ?? ''}
                onSave={handleSave}
            />
        </>
    );
}

// ── Known Issues Tab ───────────────────────────────────────────

function KnownIssuesTab({ isAdmin }: { isAdmin: boolean }) {
    const [issues, setIssues] = useState<KnownIssue[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedIssue, setSelectedIssue] = useState<KnownIssue | null>(null);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editingIssue, setEditingIssue] = useState<KnownIssue | null>(null);

    const fetchIssues = useCallback(async () => {
        setLoading(true);
        const { data, error } = await supabase
            .from('known_issues')
            .select('*')
            .order('created_at', { ascending: false });
        if (!error && data) setIssues(data);
        setLoading(false);
    }, []);

    useEffect(() => {
        fetchIssues();
    }, [fetchIssues]);

    const handleSave = async (subject: string, description: string) => {
        if (editingIssue) {
            await supabase
                .from('known_issues')
                .update({ subject, description })
                .eq('id', editingIssue.id);
        } else {
            const {
                data: { user },
            } = await supabase.auth.getUser();
            await supabase.from('known_issues').insert({
                subject,
                description,
                created_by: user?.id ?? null,
            });
        }
        fetchIssues();
    };

    const handleDelete = async (id: string) => {
        await supabase.from('known_issues').delete().eq('id', id);
        fetchIssues();
    };

    return (
        <>
            <AdminCrudTable
                items={issues}
                loading={loading}
                isAdmin={isAdmin}
                emptyIcon={
                    <AlertTriangle
                        size={40}
                        color="#cbd5e1"
                        style={{ marginBottom: 12 }}
                    />
                }
                emptyTitle="No known issues."
                emptySubtitle="Everything is running smoothly!"
                headerTitle="Known Issues"
                headerSubtitle="Current issues we're aware of and working to resolve."
                addButtonLabel="Add Issue"
                createDialogTitle="New Known Issue"
                editDialogTitle="Edit Issue"
                publishLabel="Add Issue"
                iconColor="#f59e0b"
                Icon={AlertTriangle}
                onAdd={() => {
                    setEditingIssue(null);
                    setEditDialogOpen(true);
                }}
                onEdit={item => {
                    setEditingIssue(item as KnownIssue);
                    setEditDialogOpen(true);
                }}
                onDelete={handleDelete}
                onView={item => setSelectedIssue(item as KnownIssue)}
            />

            <ViewDialog
                item={selectedIssue}
                onClose={() => setSelectedIssue(null)}
            />

            <EditDialog
                open={editDialogOpen}
                onClose={() => setEditDialogOpen(false)}
                title={editingIssue ? 'Edit Issue' : 'New Known Issue'}
                publishLabel={editingIssue ? 'Save Changes' : 'Add Issue'}
                initialSubject={editingIssue?.subject ?? ''}
                initialDescription={editingIssue?.description ?? ''}
                onSave={handleSave}
            />
        </>
    );
}

// ── Main SupportPage ───────────────────────────────────────────

export function SupportPage() {
    const { identity } = useGetIdentity();
    const [bugDialogOpen, setBugDialogOpen] = useState(false);
    const [activeTab, setActiveTab] = useState(0);
    const isAdmin = !!(identity as any)?.administrator;

    return (
        <Container maxWidth="md" sx={{ py: 4 }}>
            <Box sx={{ mb: 3, textAlign: 'center' }}>
                <Box
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 56,
                        height: 56,
                        borderRadius: '16px',
                        bgcolor: '#ecfdf5',
                        mb: 2,
                    }}
                >
                    <HelpCircle size={28} color="#10b981" />
                </Box>
                <Typography
                    variant="h4"
                    sx={{ fontWeight: 700, color: '#1e293b', mb: 1 }}
                >
                    Help & Support
                </Typography>
                <Typography
                    variant="body1"
                    sx={{ color: '#64748b', maxWidth: 480, mx: 'auto' }}
                >
                    Welcome
                    {identity?.fullName ? `, ${identity.fullName}` : ''}! Find
                    answers to common questions, learn how to use the Crop West
                    Insurance Group's Hail Pricing Tool, or report an issue
                    below.
                </Typography>
            </Box>

            <Box sx={{ borderBottom: 1, borderColor: '#e2e8f0', mb: 3 }}>
                <Tabs
                    value={activeTab}
                    onChange={(_, v) => setActiveTab(v)}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                        '& .MuiTab-root': {
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '0.95rem',
                            color: '#64748b',
                            '&.Mui-selected': { color: '#10b981' },
                        },
                        '& .MuiTabs-indicator': { backgroundColor: '#10b981' },
                    }}
                >
                    <Tab
                        label="Help & FAQ"
                        icon={
                            <HelpCircle size={18} style={{ marginRight: 6 }} />
                        }
                        iconPosition="start"
                    />
                    <Tab
                        label="Product Updates"
                        icon={
                            <Megaphone size={18} style={{ marginRight: 6 }} />
                        }
                        iconPosition="start"
                    />
                    <Tab
                        label="Known Issues"
                        icon={
                            <AlertTriangle
                                size={18}
                                style={{ marginRight: 6 }}
                            />
                        }
                        iconPosition="start"
                    />
                </Tabs>
            </Box>

            {activeTab === 0 && (
                <HelpFaqTab
                    bugDialogOpen={bugDialogOpen}
                    setBugDialogOpen={setBugDialogOpen}
                />
            )}
            {activeTab === 1 && <ProductUpdatesTab isAdmin={isAdmin} />}
            {activeTab === 2 && <KnownIssuesTab isAdmin={isAdmin} />}
        </Container>
    );
}

SupportPage.path = '/support';
